/**
 * ContestPromoTopBar — thin site-wide promo bar.
 * Original (2026-07-21). Purple migration + white-on-white fix (2026-07-23).
 * Item 3 extension (2026-07-24): ENTER NOW punch + live countdown +
 * white paw + accessor discipline.
 *
 * Renders ONLY when:
 *   * A contest round is active (via `useActiveContest`).
 *   * The user hasn't dismissed the bar THIS SESSION (in-memory
 *     state — dismissal resets on page reload / new tab).
 *
 * Mounted inside `PublicPageShell` above `<SharedNav />` so it
 * respects the impersonation-banner offset contract (Item 11 QA
 * sweep). ALSO mounted at the top of `Landing.jsx` (2026-07-24
 * Item 3 extension — the landing page uses `SharedNav` directly
 * without the shell, so the shell's copy of this bar never mounts
 * there and the bar previously never appeared on `/`).
 *
 * COLOR CONTRACT (2026-07-23 revision, extended 2026-07-24):
 *   * Bar background: `bg-pp-purple` (CSS var `--pp-purple` =
 *     `#574B8C`, theme-invariant). White message text + white
 *     dismiss icon on purple → WCAG AAA (7.51:1 measured).
 *   * "ENTER NOW" CTA: `bg-pp-orange` (`#F89623`, theme-
 *     invariant) with `text-pp-cta-text` (`#2B3942`, theme-
 *     invariant) — WCAG AA (5.21:1). Client-side `<Link>` to
 *     `/cutest-pluck` (no full-page reload).
 *   * Paw glyph: emoji-rendered 🐾 forced to pure white via the
 *     Tailwind `brightness-0 invert` filter (2026-07-24). WCAG
 *     AAA on the purple band (7.45:1 measured). Reuses the same
 *     technique as `PartnerTiersPanel.jsx:486` — no new asset.
 *   * Countdown text: white on purple, same 7.51:1 measured
 *     contrast as the copy text.
 *
 * COPY CONTRACT:
 *   * Contest name flows through `getContestDisplayName` (single
 *     source of truth — same accessor the rules page uses). Never
 *     hardcode the brand string a second time here.
 *   * "win a free Pluck" wraps in `<strong>` for the punch (2026-
 *     07-24 Item 3 extension). Other tokens stay `font-semibold`.
 *
 * COUNTDOWN CONTRACT (2026-07-24):
 *   * Reads `entryCloseAt` from `useActiveContest()` — populated
 *     by `/api/public/contest/active` (extended shape shipped
 *     2026-07-23 in commit `5971c28`).
 *   * Format: `"Entries close in Xd Yh"` (day + hour, no minutes
 *     or seconds). Freshly recomputed on mount; no interval
 *     ticking (v1 — day + hour granularity doesn't need it).
 *   * Suppressed when `entryCloseAt` is null (deploy state where
 *     the field isn't set) OR when the deadline has passed (a
 *     stale "Entries close in 0d 0h" would look broken). This
 *     failsafe means the countdown either shows a real value or
 *     nothing at all — never a placeholder.
 *   * Hidden below 480px viewport (`hidden xs:inline-flex`,
 *     custom `xs` breakpoint added in `tailwind.config.js`) so
 *     the copy + CTA stay legible on phones.
 *
 * MOTION POLICY: NONE. No pulse, no fade-in, no CSS transition
 * beyond a subtle darken on button hover (`hover:bg-pp-orange-hover`).
 * Punch = bold copy + ENTER NOW button + countdown, never motion.
 *
 * Family-L discipline: no inline hex in JSX. Tokens only. CTA is
 * a real `<Link>` (react-router). Dismiss is a real button with
 * an aria-label. Tap targets ≥ 44 × 44 preserved.
 */
import React from "react";
import { Link } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";
import { useActiveContest } from "@/context/ActiveContestContext";
import { getContestDisplayName } from "@/lib/contestConstants";

/**
 * Format the entry-close countdown. Returns `null` when the input
 * is missing, unparseable, or already in the past — the caller
 * suppresses the countdown block entirely in those cases.
 *
 * Exported for the drift-guard test (`cp-top-13..16`) so we can
 * pin the "reads config, not hardcoded date" contract with known
 * offsets. Day + hour granularity per Item 3 extension spec.
 */
export function formatEntryCloseCountdown(entryCloseAt, now = new Date()) {
  if (!entryCloseAt) return null;
  const closeMs =
    entryCloseAt instanceof Date
      ? entryCloseAt.getTime()
      : Date.parse(entryCloseAt);
  if (!Number.isFinite(closeMs)) return null;
  const nowMs = now instanceof Date ? now.getTime() : Date.parse(now);
  const remainingMs = closeMs - nowMs;
  if (remainingMs <= 0) return null;
  const totalHours = Math.floor(remainingMs / (60 * 60 * 1000));
  const days = Math.floor(totalHours / 24);
  const hours = totalHours - days * 24;
  return `Entries close in ${days}d ${hours}h`;
}

export default function ContestPromoTopBar() {
  const activeContest = useActiveContest();
  const { active, dismissed, dismiss, loading, entryCloseAt } = activeContest;

  // Self-suppress during initial fetch to avoid a "flash of promo"
  // before we know whether a contest is active.
  if (loading || !active || dismissed) return null;

  const displayName = getContestDisplayName(activeContest);
  const countdown = formatEntryCloseCountdown(entryCloseAt);

  return (
    <div
      className="bg-pp-purple text-white"
      data-testid="contest-promo-topbar"
      role="region"
      aria-label="Cutest Pluck contest promotion"
    >
      <div className="mx-auto flex max-w-[1400px] flex-wrap items-center justify-between gap-x-3 gap-y-2 px-4 py-2 text-sm sm:text-[15px]">
        <div
          className="flex min-w-0 items-center gap-2"
          data-testid="contest-promo-topbar-copy"
        >
          <span
            aria-hidden="true"
            className="brightness-0 invert"
            data-testid="contest-promo-topbar-paw"
          >
            🐾
          </span>
          <span
            className="truncate font-semibold"
            data-testid="contest-promo-topbar-copy-text"
          >
            {displayName} — enter your dog,{" "}
            <strong
              className="font-bold"
              data-testid="contest-promo-topbar-copy-strong"
            >
              win a free Pluck
            </strong>
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {/* Live countdown — hidden below xs (480px). Quiet white
              text on purple (7.51:1 contrast). No motion. */}
          {countdown ? (
            <span
              className="hidden text-xs font-medium text-white xs:inline-flex xs:items-center"
              data-testid="contest-promo-topbar-countdown"
            >
              {countdown}
            </span>
          ) : null}
          {/* Rules link — small text link BEFORE the primary CTA.
              Hidden on mobile (`hidden sm:inline-flex`) so the CTA's
              tap target isn't crowded at 320-640px viewports. */}
          <Link
            to="/cutest-pluck/rules"
            className="hidden text-xs font-medium text-white underline underline-offset-2 hover:text-white/80 sm:inline-flex sm:items-center sm:px-2"
            style={{ minHeight: 44 }}
            data-testid="contest-promo-topbar-rules-link"
          >
            Rules
          </Link>
          <Link
            to="/cutest-pluck"
            className="inline-flex items-center gap-1 rounded-full bg-pp-orange px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-pp-cta-text hover:bg-pp-orange-hover sm:text-sm"
            style={{ minHeight: 44, minWidth: 44 }}
            data-testid="contest-promo-topbar-cta"
            aria-label="Enter the Cutest Pluck contest now"
          >
            Enter now
            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </Link>
          <button
            type="button"
            onClick={dismiss}
            className="inline-flex items-center justify-center rounded-full text-white hover:bg-white/10"
            style={{ width: 44, height: 44, minWidth: 44, minHeight: 44 }}
            data-testid="contest-promo-topbar-dismiss"
            aria-label="Dismiss contest promo"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}
